import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {AutomationFileErrorInfo} from 'lab-automation/automation-files/LabAutomationConstants';
import {FilterType, LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {capitalize} from 'util/NameUtil';
import {assertNever} from 'util/Types';

import {InputFileNamePart} from './automation-input-file-config/AutomationInputFileConfigConstants';

// Keep in sync with PlateLayoutType in benchling/lab_automation/models/plate_layout.py
// Keep in sync with PlateLayoutTypedValue.plateLayout in client/notebook/tables/typed-values/TypedValueTypes.ts
export enum PlateLayoutType {
  COLUMN_INTERLEAVE = 'COLUMN_INTERLEAVE',
  INTERLEAVE = 'INTERLEAVE',
  REVERSE_COLUMN_INTERLEAVE = 'REVERSE_COLUMN_INTERLEAVE',
  REVERSE_INTERLEAVE = 'REVERSE_INTERLEAVE',
  REVERSE_STAMP_TO_QUADRANTS = 'REVERSE_STAMP_TO_QUADRANTS',
  STAMP = 'STAMP',
  STAMP_TO_QUADRANTS = 'STAMP_TO_QUADRANTS',
}

// Keep in sync with readable_plate_layout in benchling/lab_automation/models/plate_layout.py
export function readablePlateLayout(layoutType: PlateLayoutType): string {
  return capitalize(layoutType.replace(/_/g, ' ').toLowerCase());
}

export function stringToPlateLayout(layoutTypeString: string): PlateLayoutType {
  return layoutTypeString.replace(/\s/g, '_').toUpperCase() as PlateLayoutType;
}

interface PlateLayoutSpec {
  layoutType: PlateLayoutType;
}

// Keep in sync with AutomationInputFileRowConfigSpecEditor in benchling/lab_automation/spec/automation_input_file_row_config_spec_editor.py
export interface RowConfigSpec {
  source: LookupConfigSpec;
  destination?: string;
  columnsMap: {[columnName: string]: LookupConfigSpec};
  plateLayout?: PlateLayoutSpec;
}

export const labAutomationFileExtensionOptions: {[extension: string]: string} = {
  csv: '.csv',
  tsv: '.tsv',
  txt: '.txt',
};

export const labAutomationFileEncodingOptions: {[encoding: string]: string} = {
  'utf-8': 'UTF-8',
  'utf-16-le': 'UTF-16 LE (Unicode)',
};

export const labAutomationFileDelimiterOptions: {[delimiter: string]: string} = {
  ',': 'Comma (",")',
  ' ': 'Space (" ")',
  '\t': 'Tab ("\xa0\xa0\xa0\xa0")',
  '|': 'Pipe ("|")',
};

export interface DestinationInfosSpec {
  [destinationName: string]: {lookupConfig: LookupConfigSpec};
}

// Keep in sync with AutomationInputFileConfigSpecEditor in benchling/lab_automation/spec/automation_input_file_row_config_spec_editor.py
export interface AutomationInputFileConfigSpec {
  name: string;
  columns: Array<string>;
  sortColumns: Array<string>;
  destinationInfos?: DestinationInfosSpec;
  rowConfigs: Array<RowConfigSpec>;
  delimiter: string;
  fileExtension: string;
  fileEncoding?: string;
  isHidden?: boolean;
  transforms?: Array<LabAutomationTransform>;
  nameTemplateParts?: Array<InputFileNamePart>;
}

// Should be kept in sync with AutomationOutputFileGranularActionType
export enum OutputFileAction {
  NO_ACTION = 'NO_ACTION',
  PURE_REGISTRATION = 'PURE_REGISTRATION',
  REGISTRATION_AND_RESULTS = 'REGISTRATION_AND_RESULTS',
  TRANSFORMATION_TRANSFER = 'TRANSFORMATION_TRANSFER',
  TRANSFORMATION_TRANSFER_AND_RESULTS = 'TRANSFORMATION_TRANSFER_AND_RESULTS',
  PURE_TRANSFER = 'PURE_TRANSFER',
  TRANSFER_AND_RESULTS = 'TRANSFER_AND_RESULTS',
  PURE_RESULTS = 'PURE_RESULTS',
  RUN_ANALYSIS_TEMPLATE = 'RUN_ANALYSIS_TEMPLATE',
}

export const REGISTRATION_ACTION_TYPES = new Set([
  OutputFileAction.TRANSFORMATION_TRANSFER,
  OutputFileAction.TRANSFORMATION_TRANSFER_AND_RESULTS,
  OutputFileAction.PURE_REGISTRATION,
  OutputFileAction.REGISTRATION_AND_RESULTS,
]);

export const RESULTS_ACTION_TYPES = new Set([
  OutputFileAction.PURE_RESULTS,
  OutputFileAction.REGISTRATION_AND_RESULTS,
  OutputFileAction.TRANSFER_AND_RESULTS,
  OutputFileAction.TRANSFORMATION_TRANSFER_AND_RESULTS,
]);

export const INVENTORY_ACTION_TYPES = new Set([
  OutputFileAction.TRANSFORMATION_TRANSFER,
  OutputFileAction.TRANSFORMATION_TRANSFER_AND_RESULTS,
  OutputFileAction.PURE_TRANSFER,
  OutputFileAction.TRANSFER_AND_RESULTS,
]);

export function isTransfer(actionType: OutputFileAction, shouldPerformPlateTransfer: boolean): boolean {
  return INVENTORY_ACTION_TYPES.has(actionType) || shouldPerformPlateTransfer;
}

// Keep in sync with benchling/lab_automation/enums.py:AutomationOutputFileTableType
export enum AutomationOutputFileTableType {
  GRID = 'GRID',
  SAMPLE = 'SAMPLE',
}

// Keep in sync with benchling/lab_automation/enums.py:AutomationOutputFileTableSortType
export enum AutomationOutputFileTableSortType {
  ALPHABETICAL = 'ALPHABETICAL',
  UPLOADED = 'UPLOADED',
}

export const labAutomationStructuredTableTypeOptions: {
  [action in AutomationOutputFileTableType]: string;
} = {
  GRID: 'One row per plate',
  SAMPLE: 'One row per sample',
};

export const labAutomationStructuredTableSortTypeOptions: {
  [action in AutomationOutputFileTableSortType]: string;
} = {
  ALPHABETICAL: 'Alphabetical order',
  UPLOADED: 'Preserve uploaded file order',
};

// Keep in sync with TransformDataType in packages/benchling-asm-parser/src/benchling_asm_parser/constants.py
export enum AutomationOutputFileTransformDataType {
  ALLOTROPE = 'ALLOTROPE',
  COMPENSATION = 'COMPENSATION',
  CUSTOM = 'CUSTOM',
  DATACUBE = 'DATACUBE',
  FRACTIONS = 'FRACTIONS',
  GROUP = 'GROUP',
  INJECTIONS = 'INJECTIONS',
  MEASUREMENT = 'MEASUREMENT',
  MEASUREMENT_SEPARATE_UNIT_COLUMN = 'MEASUREMENT_SEPARATE_UNIT_COLUMN',
  ORIGINAL = 'ORIGINAL',
  PEAKS = 'PEAKS',
  PROCESSED = 'PROCESSED',
  POPULATION = 'POPULATION',
  REPORT_POINT = 'REPORT_POINT',
  REGION = 'REGION',
  REPORT = 'REPORT',
  SAMPLE = 'SAMPLE',
  SAMPLE_SEPARATE_UNIT_COLUMN = 'SAMPLE_SEPARATE_UNIT_COLUMN',
  STATISTICS = 'STATISTICS',
  TIMESERIES = 'TIMESERIES',
  WELL = 'WELL',
}

// Keep in sync with benchling/lab_automation/models/automation_output_file_transformation_field_info.py:AutomationOutputFileTransformationFieldInfo.TransformationFieldType
export enum TransformationFieldType {
  CALCULATE_FINAL_CONCENTRATION = 'CALCULATE_FINAL_CONCENTRATION',
  PRIMARY_SAMPLE_ENTITY = 'PRIMARY_SAMPLE_ENTITY',
  DIRECT = 'DIRECT',
  SOURCE_CONTENTS = 'SOURCE_CONTENTS',
}
interface BaseDirectTransformationFieldInfo {
  type: TransformationFieldType.DIRECT;
}

export interface DirectTransformationFieldInfoWithColumnName extends BaseDirectTransformationFieldInfo {
  columnName?: string;
}

export interface DirectTransformationFieldInfoWithColumnIndex extends BaseDirectTransformationFieldInfo {
  columnIndex?: number;
}

export type DirectTransformationFieldInfo =
  | DirectTransformationFieldInfoWithColumnName
  | DirectTransformationFieldInfoWithColumnIndex;

interface PrimarySampleEntityTransformationFieldInfo {
  type: TransformationFieldType.PRIMARY_SAMPLE_ENTITY;
}

export interface SourceContentsTransformationFieldInfo {
  type: TransformationFieldType.SOURCE_CONTENTS;
  schema?: string | null;
}

export interface CalculateFinalConcentrationTransformationFieldInfo {
  type: TransformationFieldType.CALCULATE_FINAL_CONCENTRATION;
  schema?: string | null;
}

export type TransformationFieldInfo =
  | DirectTransformationFieldInfo
  | PrimarySampleEntityTransformationFieldInfo
  | SourceContentsTransformationFieldInfo
  | CalculateFinalConcentrationTransformationFieldInfo;

export interface SchemaInfoFields {
  [fieldName: string]: TransformationFieldInfo;
}

// Keep in sync with benchling/lab_automation/models/automation_output_file_tag_schema_info.py:TagSchemaInfoActionType
export enum TagSchemaInfoActionType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  CREATE_OR_UPDATE = 'CREATE_OR_UPDATE',
}

export interface AutomationOutputFileEntitySchemaInfo {
  schema: string | null;
  folder?: string;
  labelingStrategy: RegistryLabelingStrategy;
  shouldReplaceExisting?: boolean;
  actionType: TagSchemaInfoActionType;
  fields: SchemaInfoFields;
}

export interface AutomationOutputFileResultSchemaInfo {
  schema: string | null;
  fields: SchemaInfoFields;
}

interface BaseAutomationOutputFileDestinationInfo {
  location?: string | null;
  project?: string | null;
  fields: SchemaInfoFields;
}

interface AutomationOutputFileDestinationInfoWithContainerSchema extends BaseAutomationOutputFileDestinationInfo {
  containerSchema: string | null;
  boxSchema?: never;
  plateSchema?: never;
}

interface AutomationOutputFileDestinationInfoWithBoxSchema extends BaseAutomationOutputFileDestinationInfo {
  boxSchema: string | null;
  containerSchema?: never;
  plateSchema?: never;
}

interface AutomationOutputFileDestinationInfoWithPlateSchema extends BaseAutomationOutputFileDestinationInfo {
  plateSchema: string | null;
  containerSchema?: never;
  boxSchema?: never;
}

export type AutomationOutputFileDestinationInfo =
  | AutomationOutputFileDestinationInfoWithContainerSchema
  | AutomationOutputFileDestinationInfoWithBoxSchema
  | AutomationOutputFileDestinationInfoWithPlateSchema;

export type AutomationOutputFileSchemaInfo =
  | AutomationOutputFileDestinationInfo
  | AutomationOutputFileEntitySchemaInfo
  | AutomationOutputFileResultSchemaInfo;

// Keep in sync with benchling/lab_automation/models/automation_output_file_config.py:AutomationOutputFileConfigColumnType
export enum AutomationOutputFileColumnType {
  ALIAS = 'ALIAS',
  CUSTOM_NOTATION = 'CUSTOM_NOTATION',
  DATASET = 'DATASET', // DEPRECATED
  ENTITY_API_ID = 'ENTITY_API_ID',
  ENTITY_NAME = 'ENTITY_NAME',
  FINAL_QUANTITY_AMOUNT = 'FINAL_VOLUME_AMOUNT',
  SOURCE_FINAL_QUANTITY_AMOUNT = 'SOURCE_FINAL_VOLUME_AMNT',
  HELM = 'HELM',
  IGNORE = 'IGNORE',
  INPUT_BOX_BARCODE = 'INPUT_BOX_BARCODE',
  INPUT_BOX_COORDINATES = 'INPUT_BOX_COORDINATES',
  INPUT_CONCENTRATION_VALUE = 'INPUT_CONCENTRATION_VALUE',
  FINAL_CONCENTRATION_VALUE = 'FINAL_CONCENTRATION_VALUE',
  INPUT_CONTAINER_BARCODE = 'INPUT_CONTAINER_BARCODE',
  INPUT_ENTITY_NAME = 'INPUT_ENTITY_NAME',
  INPUT_PLATE_BARCODE = 'INPUT_PLATE_BARCODE',
  INPUT_PLATE_COORDINATES = 'INPUT_PLATE_COORDINATES',
  MOLFILE = 'MOLFILE',
  NEW_ENTITY_NAME = 'NEW_ENTITY_NAME',
  OUTPUT_BOX_BARCODE = 'OUTPUT_BOX_BARCODE',
  OUTPUT_BOX_COORDINATES = 'OUTPUT_BOX_COORDINATES',
  OUTPUT_CONTAINER_BARCODE = 'OUTPUT_CONTAINER_BARCODE',
  OUTPUT_PLATE_BARCODE = 'OUTPUT_PLATE_BARCODE',
  OUTPUT_PLATE_COORDINATES = 'OUTPUT_PLATE_COORDINATES',
  PROCEDURE_CONDITION_REPLICATES = 'PROCEDURE_CONDITION_REPLICATES',
  RESIDUES = 'RESIDUES',
  SCHEMA_FIELD = 'SCHEMA_FIELD',
  SMILES = 'SMILES',
  TRANSFER_QUANTITY_AMOUNT = 'TRANSFER_VOLUME_AMOUNT',
}

export const STORAGE_COLUMN_TYPES = [
  AutomationOutputFileColumnType.INPUT_BOX_BARCODE,
  AutomationOutputFileColumnType.INPUT_BOX_COORDINATES,
  AutomationOutputFileColumnType.INPUT_CONCENTRATION_VALUE,
  AutomationOutputFileColumnType.FINAL_CONCENTRATION_VALUE,
  AutomationOutputFileColumnType.INPUT_CONTAINER_BARCODE,
  AutomationOutputFileColumnType.INPUT_PLATE_BARCODE,
  AutomationOutputFileColumnType.INPUT_PLATE_COORDINATES,
  AutomationOutputFileColumnType.OUTPUT_BOX_BARCODE,
  AutomationOutputFileColumnType.OUTPUT_BOX_COORDINATES,
  AutomationOutputFileColumnType.OUTPUT_CONTAINER_BARCODE,
  AutomationOutputFileColumnType.OUTPUT_PLATE_BARCODE,
  AutomationOutputFileColumnType.OUTPUT_PLATE_COORDINATES,
];

// Keep in sync with AutomationTransformType
export enum LabAutomationTransformType {
  ADD_COLUMN = 'ADD_COLUMN',
  ARITHMETIC = 'ARITHMETIC',
  CUSTOM = 'CUSTOM',
  DUPLICATE = 'DUPLICATE',
  EXTRA_HEADERS = 'EXTRA_HEADERS',
  EXTRA_FOOTERS = 'EXTRA_FOOTERS',
  FILTER = 'FILTER',
  FIND_REPLACE = 'FIND_REPLACE',
  MERGE = 'MERGE',
  MELT = 'MELT',
  PLATE_SHAPE = 'PLATE_SHAPE',
  PIVOT = 'PIVOT',
  REMOVE_QUOTES = 'REMOVE_QUOTES',
  SPLIT = 'SPLIT',
}

export interface AddColumnTransform {
  transformType: LabAutomationTransformType.ADD_COLUMN;
  transformationProperties: {
    column?: string;
  };
  lookupConfig?: LookupConfigSpec;
}

export enum ArithmeticOperator {
  ADDITION = '+',
  SUBTRACTION = '-',
  MULTIPLICATION = '*',
  DIVISION = '/',
}

export enum ArithmeticVariableType {
  CONSTANT = 'CONSTANT',
  COLUMN = 'COLUMN',
}

export interface ArithmeticTransform {
  transformType: LabAutomationTransformType.ARITHMETIC;
  transformationProperties: {
    operator?: ArithmeticOperator;
    newColumnName?: string;
    firstVariable?: {
      type?: ArithmeticVariableType;
      value?: string | number;
    };
    secondVariable?: {
      type?: ArithmeticVariableType;
      value?: string | number;
    };
  };
}

export interface CustomTransform {
  transformType: LabAutomationTransformType.CUSTOM;
  transformationProperties: {
    transformId?: string;
  };
}

export interface DuplicateTransform {
  transformType: LabAutomationTransformType.DUPLICATE;
  transformationProperties: {columnToDuplicate?: string; newColumnName?: string};
}

export interface ExtraHeadersTransform {
  transformType: LabAutomationTransformType.EXTRA_HEADERS;
  transformationProperties: {
    headerRowIndex?: number;
    lastHeaderRowIndex?: number;
  };
}

export interface ExtraFootersTransform {
  transformType: LabAutomationTransformType.EXTRA_FOOTERS;
  transformationProperties: {
    numRowsToRemove?: number;
  };
}

export interface FilterTransform {
  transformType: LabAutomationTransformType.FILTER;
  transformationProperties: {
    column?: string;
    type?: FilterType;
    value?: string | number;
    values?: Array<string>;
  };
  lookupConfig?: LookupConfigSpec;
}

export interface FindReplaceTransform {
  transformType: LabAutomationTransformType.FIND_REPLACE;
  transformationProperties: {
    columns?: Array<string>;
    comparisonType?: FilterType;
    findValue?: string | number;
    replaceValue?: string | number;
  };
  lookupConfig?: LookupConfigSpec;
}
export interface MergeTransform {
  transformType: LabAutomationTransformType.MERGE;
  transformationProperties: {
    columnNames?: Array<string>;
    outputColumnName?: string;
    delimiter: string;
  };
}
export interface MeltTransform {
  transformType: LabAutomationTransformType.MELT;
  transformationProperties: {
    identifierColumns?: Array<string>;
    valueColumns?: Array<string>;
    newVariableColumn?: string;
    newValueColumn?: string;
  };
}
export interface PlateShapeTransform {
  transformType: LabAutomationTransformType.PLATE_SHAPE;
  transformationProperties: {
    plateSchema?: string;
    firstWellRowIndex?: number;
    firstWellColumnIndex?: number;
    deleteOtherCells: boolean;
  };
}

export interface PivotTransform {
  transformType: LabAutomationTransformType.PIVOT;
  transformationProperties: {
    indexColumns?: Array<string>;
    keysColumn?: string;
    valuesColumn?: string;
  };
}

export interface RemoveQuotesTransform {
  transformType: LabAutomationTransformType.REMOVE_QUOTES;
  transformationProperties: {};
}

export interface SplitTransform {
  transformType: LabAutomationTransformType.SPLIT;
  transformationProperties: {
    column?: string;
    outputColumnNames?: Array<string | null>;
    delimiter?: string;
  };
}

interface EmptyTransform {
  transformType: null;
}

export type LabAutomationTransform =
  | AddColumnTransform
  | ArithmeticTransform
  | CustomTransform
  | DuplicateTransform
  | ExtraHeadersTransform
  | ExtraFootersTransform
  | FilterTransform
  | FindReplaceTransform
  | MergeTransform
  | MeltTransform
  | PlateShapeTransform
  | PivotTransform
  | RemoveQuotesTransform
  | SplitTransform
  | EmptyTransform;

interface TransformPreviewFileWithErrors {
  url: null;
  errors: AutomationFileErrorInfo;
}

interface TransformPreviewFileWithUrl {
  url: string;
  errors: null;
}

export type TransformPreviewFile = TransformPreviewFileWithUrl | TransformPreviewFileWithErrors;

export enum SetColumnsMappingType {
  BY_COLUMN_NAME = 'BY_COLUMN_NAME',
  BY_COLUMN_ORDER = 'BY_COLUMN_ORDER',
}

export const labAutomationSetColumnsMappingTypeOptions: {
  [action in SetColumnsMappingType]: string;
} = {
  BY_COLUMN_NAME: 'By column name',
  BY_COLUMN_ORDER: 'By column order',
};

interface BaseAutomationOutputFileConfigSpec {
  name: string;
  delimiter: string;
  fileExtension: string;
  tableType: AutomationOutputFileTableType;
  tableSortType: AutomationOutputFileTableSortType;
  actionType: OutputFileAction;
  entitySchemaInfo?: AutomationOutputFileEntitySchemaInfo | null;
  resultSchemaInfo?: AutomationOutputFileResultSchemaInfo | null;
  destinationInfo?: AutomationOutputFileDestinationInfo | null;
  spreadsheetProcessingLocale: string;
  transforms?: Array<LabAutomationTransform>;
  volumeUnits?: string;
  concentrationUnits?: string;
  isHidden?: boolean;
  transformDataType?: AutomationOutputFileTransformDataType | null;
  customSyntaxId?: string;
  shouldCreateDataset: boolean;
  shouldAllowAttachment: boolean;
  shouldPerformPlateTransfer: boolean;
  shouldRecordImages?: boolean;
  datasetNameTemplateParts?: Array<InputFileNamePart>;
  datasetConfigJson?: string;
  jsonMapperConfigJson?: string;
  analysisTemplate?: string | null;
  tabName?: string | null;
}

export function getColumnTypeDisplayName(columnType: AutomationOutputFileColumnType): string {
  switch (columnType) {
    case AutomationOutputFileColumnType.ALIAS:
      return 'Alias';
    case AutomationOutputFileColumnType.CUSTOM_NOTATION:
      return 'Custom notation';
    case AutomationOutputFileColumnType.DATASET:
      return 'Dataset';
    case AutomationOutputFileColumnType.ENTITY_API_ID:
      return 'Entity API ID';
    case AutomationOutputFileColumnType.ENTITY_NAME:
      return 'Existing entity name';
    case AutomationOutputFileColumnType.FINAL_QUANTITY_AMOUNT:
      return `Final ${getQuantityDisplayString(false)}`;
    case AutomationOutputFileColumnType.SOURCE_FINAL_QUANTITY_AMOUNT:
      return `Source container final ${getQuantityDisplayString(false)}`;
    case AutomationOutputFileColumnType.HELM:
      return 'HELM';
    case AutomationOutputFileColumnType.IGNORE:
      return 'Ignore';
    case AutomationOutputFileColumnType.INPUT_BOX_BARCODE:
      return 'Source box barcode';
    case AutomationOutputFileColumnType.INPUT_BOX_COORDINATES:
      return 'Source box coordinates';
    case AutomationOutputFileColumnType.INPUT_CONCENTRATION_VALUE:
      return 'Source concentration';
    case AutomationOutputFileColumnType.FINAL_CONCENTRATION_VALUE:
      return 'Final concentration';
    case AutomationOutputFileColumnType.INPUT_CONTAINER_BARCODE:
      return 'Source container barcode';
    case AutomationOutputFileColumnType.INPUT_ENTITY_NAME:
      return 'Source entity name';
    case AutomationOutputFileColumnType.INPUT_PLATE_BARCODE:
      return 'Source plate barcode';
    case AutomationOutputFileColumnType.INPUT_PLATE_COORDINATES:
      return 'Source plate coordinates';
    case AutomationOutputFileColumnType.MOLFILE:
      return 'Molfile';
    case AutomationOutputFileColumnType.NEW_ENTITY_NAME:
      return 'New entity name';
    case AutomationOutputFileColumnType.OUTPUT_BOX_BARCODE:
      return 'Destination box barcode';
    case AutomationOutputFileColumnType.OUTPUT_BOX_COORDINATES:
      return 'Destination box coordinates';
    case AutomationOutputFileColumnType.OUTPUT_CONTAINER_BARCODE:
      return 'Destination container barcode';
    case AutomationOutputFileColumnType.OUTPUT_PLATE_BARCODE:
      return 'Destination plate barcode';
    case AutomationOutputFileColumnType.OUTPUT_PLATE_COORDINATES:
      return 'Destination plate coordinates';
    case AutomationOutputFileColumnType.PROCEDURE_CONDITION_REPLICATES:
      return 'Condition - Replicate';
    case AutomationOutputFileColumnType.RESIDUES:
      return 'Residues';
    case AutomationOutputFileColumnType.SMILES:
      return 'SMILES';
    case AutomationOutputFileColumnType.SCHEMA_FIELD:
      return 'Schema Field';
    case AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT:
      return `Transfer ${getQuantityDisplayString(false)}`;
    default:
      throw new Error(`Unexpected output file column type ${assertNever(columnType)}`);
  }
}

interface AutomationOutputFileConfigSpecWithColumnTypesArray extends BaseAutomationOutputFileConfigSpec {
  columnTypes: Array<AutomationOutputFileColumnType>;
  columnTypesByName?: null;
}

export interface ColumnTypesByName {
  [name: string]: {columnType: AutomationOutputFileColumnType; isRequired?: boolean};
}

interface AutomationOutputFileConfigSpecWithColumnTypesByName extends BaseAutomationOutputFileConfigSpec {
  columnTypes?: null;
  columnTypesByName: ColumnTypesByName;
}

export enum AutomationOutputFileConfigSchemaInfoType {
  DATASET_INFO = 'DATASET_INFO',
  DESTINATION_INFO = 'DESTINATION_INFO',
  ENTITY_SCHEMA_INFO = 'ENTITY_SCHEMA_INFO',
  RESULT_SCHEMA_INFO = 'RESULT_SCHEMA_INFO',
}

interface BaseColumnTypeMetadata {
  columnType?: AutomationOutputFileColumnType;
  name?: string;
  schemaField?: undefined;
  schemaInfoType?: undefined;
  schemaId?: undefined;
  isFieldRequired?: undefined;
  isRequired?: boolean;
  customSyntaxId?: string;
}

export interface SchemaFieldColumnTypeMetadata {
  columnType: AutomationOutputFileColumnType;
  name?: string;
  schemaField: string;
  schemaInfoType: AutomationOutputFileConfigSchemaInfoType;
  schemaId: string | null;
  isFieldRequired: boolean;
  isRequired: boolean;
}

export type AutomationOutputFileColumnTypeMetadata = BaseColumnTypeMetadata | SchemaFieldColumnTypeMetadata;

export type AutomationOutputFileConfigSpec =
  | AutomationOutputFileConfigSpecWithColumnTypesArray
  | AutomationOutputFileConfigSpecWithColumnTypesByName;

export enum DestinationInfoAction {
  CREATE_OR_UPDATE = 'CREATE_OR_UPDATE',
  USE_EXISTING = 'USE_EXISTING',
}

export type ClientAutomationOutputFileConfigSpec = AutomationOutputFileConfigSpec & {
  actionType: OutputFileAction;
  destinationActionType?: DestinationInfoAction;
};

export interface FileConfigErrors {
  generic: Array<string>;
  [componentIndex: number]: Array<string>;
}

export type AutomationFileConfigSpec = AutomationInputFileConfigSpec | ClientAutomationOutputFileConfigSpec;
